import { Dispatch, SetStateAction } from "react";
import { Select, MenuItem, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import IContact from "../../../../models/contact";
import { IPropertyGroup } from "../../../../models/propertyGroup";
import { EntryWrapper, EntryTitle, EntryField } from "../../../customStyles/form";
import { validEmailRegex, validPhoneRegex } from "../../../../utils/regex";

export function updateContactState<T extends IContact>(
  setContactState: Dispatch<SetStateAction<T>>,
  field: keyof T,
  value: T[keyof T]
) {
  setContactState((prevState: T) => ({
    ...prevState,
    [field]: value,
  }));
}

interface IContactFormFieldsProps {
  contact: IContact;
  setContact: Dispatch<SetStateAction<IContact>>;
  propertyGroups: IPropertyGroup[];
}

const ContactFormFields = ({
  contact,
  setContact,
  propertyGroups
}: IContactFormFieldsProps) => {
  const { t } = useTranslation();
  return (
    <>
      <EntryWrapper>
        <EntryTitle>{t("table.contact.name")} *</EntryTitle>
        <EntryField>
          <TextField
            required={true}
            style={{
              textAlign: "left",
              width: "100%",
            }}
            value={contact.name}
            onChange={(e) =>
              updateContactState(
                setContact,
                "name",
                e.target.value
              )
            }
          />
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle>{t("table.property.propertyGroups")} *</EntryTitle>
        <EntryField>
          <Select
            required={true}
            labelId="property-select-label"
            multiple
            value={contact.propertyGroupIds ?? []}
            sx={{ width: "100%", height: "2em" }}
            MenuProps={{
              style: { overflowY: "visible" },
            }}
            onChange={(e) =>
              updateContactState(
                setContact,
                "propertyGroupIds",
                e.target.value as number[]
              )
            }
            renderValue={(selected) => {
              if (selected.length === 0) return "";

              return (selected)
                .map((id) => {
                  const group = propertyGroups.find(
                    (b) => b.id === id
                  );
                  return group ? group.name : "";
                })
                .filter((name) => name !== "")
                .join(", ");
            }}
          >

            {propertyGroups.map((propertyGroup) => (
              <MenuItem key={propertyGroup.id} value={propertyGroup.id}>
                {propertyGroup.name}
              </MenuItem>
            ))}
          </Select>
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle>{t("table.contact.positionFrench")} *</EntryTitle>
        <EntryField>
          <TextField
            required={true}
            style={{
              textAlign: "left",
              width: "100%",
            }}
            value={contact.positionFrench}
            onChange={(e) =>
              updateContactState(
                setContact,
                "positionFrench",
                e.target.value
              )
            }
          />
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle>{t("table.contact.positionEnglish")} </EntryTitle>
        <EntryField>
          <TextField
            style={{
              textAlign: "left",
              width: "100%",
            }}
            value={contact.positionEnglish}
            onChange={(e) =>
              updateContactState(
                setContact,
                "positionEnglish",
                e.target.value
              )
            }
          />
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle>{t("table.contact.positionSpanish")} </EntryTitle>
        <EntryField>
          <TextField
            style={{
              textAlign: "left",
              width: "100%",
            }}
            value={contact.positionSpanish}
            onChange={(e) =>
              updateContactState(
                setContact,
                "positionSpanish",
                e.target.value
              )
            }
          />
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle>{t("table.contact.email")} </EntryTitle>
        <EntryField>
          <TextField
            style={{
              textAlign: "left",
              width: "100%",
            }}
            type="email"
            error={
              !!contact.email &&
              !validEmailRegex.test(contact.email!)
            }
            value={contact.email}
            onChange={(e) =>
              updateContactState(
                setContact,
                "email",
                e.target.value
              )
            }
          />
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle>{t("table.contact.phone")} </EntryTitle>
        <EntryField>
          <TextField
            style={{
              textAlign: "left",
              width: "100%",
            }}
            type="tel"
            error={
              !!contact.phone &&
              !validPhoneRegex.test(contact.phone!)
            }
            value={contact.phone}
            onChange={(e) =>
              updateContactState(
                setContact,
                "phone",
                e.target.value
              )
            }
          />
        </EntryField>
      </EntryWrapper>
    </>
  );
};

export default ContactFormFields;
